import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import ReactModal from "react-modal";

export interface ThemeContextShape {
  isGlobalDarkMode: boolean;
  toggleGlobalDarkMode: () => void;
  fontFamily: string;
  setFontFamily: (newFont: string) => void;
  appRef: React.RefObject<HTMLElement>;
}

const defaultContext: ThemeContextShape = {
  isGlobalDarkMode: false,
  toggleGlobalDarkMode: () => {},
  fontFamily: "",
  setFontFamily: () => {},
  appRef: { current: null },
};

export const ThemeContext = createContext<ThemeContextShape>(defaultContext);

const STORAGE_KEY_THEME = "@peak-security-suite/ui-elements:theme";

export interface ThemeProviderProps {
  children: React.ReactNode;
  fontFamily: string;
  appElement?: string;
  initialGlobalDarkMode?: boolean;
  recoverDarkModePref?: boolean;
}

export const ThemeProvider = ({
  children,
  fontFamily,
  appElement = "#root",
  initialGlobalDarkMode = false,
  recoverDarkModePref = true,
}: ThemeProviderProps) => {
  const [isGlobalDarkMode, setIsGlobalDarkMode] = useState(
    initialGlobalDarkMode
  );
  const [fontFamilyState, setFontFamilyState] = useState(fontFamily);
  const appRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    let darkMode = initialGlobalDarkMode;
    if (recoverDarkModePref) {
      const recoveredMode = localStorage.getItem(STORAGE_KEY_THEME);
      if (recoveredMode) darkMode = recoveredMode === "dark";
    }
    if (darkMode) {
      setIsGlobalDarkMode(true);
      document.documentElement.classList.add("dark");
    } else {
      setIsGlobalDarkMode(false);
      document.documentElement.classList.remove("dark");
    }
  }, []);

  useEffect(() => {
    fontFamilyState &&
      document.documentElement.style.setProperty(
        "--font-family",
        fontFamilyState
      );
  }, [fontFamilyState]);

  useEffect(() => {
    ReactModal.setAppElement(appElement);
    appRef.current = document.querySelector(appElement);
  }, [appElement]);

  const toggleGlobalDarkMode = () => {
    const newTheme = !isGlobalDarkMode;
    setIsGlobalDarkMode(newTheme);
    if (newTheme) {
      document.documentElement.classList.add("dark");
      recoverDarkModePref && localStorage.setItem(STORAGE_KEY_THEME, "dark");
    } else {
      document.documentElement.classList.remove("dark");
      recoverDarkModePref && localStorage.setItem(STORAGE_KEY_THEME, "light");
    }
  };

  const setFontFamily = (newFont) => {
    setFontFamilyState(newFont);
  };

  const value: ThemeContextShape = {
    isGlobalDarkMode,
    toggleGlobalDarkMode,
    fontFamily: fontFamilyState,
    setFontFamily,
    appRef,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextShape => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
